import { motion } from "framer-motion";
import { Waves, Brain, TrendingUp, Tags } from "lucide-react";

const FEATURES = [
  { icon: Waves, title: "Call transcription", desc: "Every inbound & outbound call automatically transcribed and summarized." },
  { icon: Tags, title: "Intent & outcome", desc: "AI classifies booking vs info, sentiment, keywords and call outcome." },
  { icon: Brain, title: "Patient insights", desc: "Surface adherence risks, objections and opportunities from real conversations." },
  { icon: TrendingUp, title: "Performance signals", desc: "Know which campaigns, scripts and staff convert — and which leak." },
];

export const ConversationIntelligence = () => (
  <section data-testid="conversation-intelligence-section" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="overflow-hidden rounded-3xl border border-border bg-card">
        <div className="grid lg:grid-cols-2">
          <div className="relative p-8 sm:p-12">
            <div className="absolute -left-10 top-10 h-40 w-40 rounded-full bg-fuchsia-500/20 blur-3xl" />
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-insights">
              Conversation Intelligence
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Turn every call into <span className="text-gradient">patient insight</span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              ClinicFirst listens, transcribes and analyzes every conversation — giving owners
              a real-time pulse on patient intent, objections and team performance.
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-fuchsia-500/15 text-brand-insights">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-3 font-display text-base font-bold">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="relative flex items-center justify-center border-t border-border bg-gradient-to-br from-fuchsia-500/5 to-violet-500/5 p-8 sm:p-12 lg:border-l lg:border-t-0">
            <div className="w-full max-w-sm rounded-2xl border border-border bg-background/80 p-5 shadow-lg backdrop-blur">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Live call · 04:12
              </div>
              <div className="mt-4 space-y-3">
                {[
                  { side: "Patient", text: "Do you have anything this Friday for lower back pain?" },
                  { side: "AI", text: "Yes — Dr. Sarah has 3pm open. Shall I book it?" },
                ].map((m) => (
                  <div key={m.text} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.side === "AI" ? "ml-auto bg-gradient-to-r from-blue-600 to-violet-600 text-white" : "bg-secondary"}`}>
                    {m.text}
                  </div>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                {[["Intent", "Booking"], ["Sentiment", "Positive"], ["Outcome", "Booked"]].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-secondary p-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className="text-xs font-bold text-brand-insights">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);
